import { useEffect, useState } from 'react';
import { Button, Field, Icon, Input } from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';
import { AuthShell } from './AuthShell.tsx';

/**
 * Spending a reset link.
 *
 * ── THE TOKEN COMES OUT OF THE FRAGMENT ──────────────────────────────────
 *
 * `/reset-password#token=…`, never `?token=…`. A query string reaches the
 * server, the access log, any proxy in between and the `Referer` header of the
 * next request the page makes. A fragment is never transmitted. This token
 * sets a console password, so it is the one that matters most.
 *
 * Read once on mount and then REMOVED from the address bar, so a screenshot,
 * a shared screen or a browser-history entry does not carry it.
 *
 * ── AND THE SCREEN SAYS WHAT HAPPENS NEXT, BEFORE IT HAPPENS ─────────────
 *
 * Setting the password signs every session out, un-remembers every trusted
 * device, and does NOT sign this one in. All three are surprising unless said:
 * an administrator who expects to land on the dashboard and arrives at a
 * sign-in wall reads it as a failure, one who does not know their other
 * sessions ended will not understand why their second screen stopped working,
 * and one whose own laptop starts asking for a code again will think the reset
 * broke something.
 *
 * The device sentence is also the visible face of a security property: a
 * remembered browser SKIPS the second factor, so a reset that left it
 * remembered would leave that one browser needing nothing but the new password.
 * Saying it on screen is what stops somebody later "fixing" the inconvenience.
 */
export function ResetPassword() {
  const [preview, setPreview] = useState<{
    email: string;
    displayName: string;
    role: string;
    passwordMinimum: number;
  } | null>(null);
  const [deadLink, setDeadLink] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<{ sessionsEnded: number; trustedDevicesEnded: number } | null>(
    null,
  );

  /*
   * READ DURING RENDER, CLEARED IN AN EFFECT — and the order is the whole bug
   * this shape exists to avoid.
   *
   * The first version did both in one `useEffect`: read the fragment, then
   * `replaceState` it away. That is an effect which DESTROYS ITS OWN INPUT, so
   * React's development double-invoke ran it twice — the first pass read the
   * token and removed it from the URL, the second pass read an empty hash and
   * set the token to null. The screen said "that is not a reset link" while
   * holding a perfectly good one. Found by walking it in a browser; nothing in
   * the type system or the tests could see it, and it would have recurred on
   * any remount in production too.
   *
   * A lazy `useState` initialiser runs in the RENDER phase, before any effect,
   * so both of StrictMode's render passes see the intact fragment and agree.
   * The clearing then happens once, afterwards, and re-running it is a no-op.
   *
   * `window.location.hash` rather than a router hook, for the reason the
   * player portal's reset gives: this screen is matched on the path in
   * `main.tsx` and never mounts under a router.
   */
  const [token] = useState<string | null>(() =>
    new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token'),
  );

  /* Out of the address bar, so a screenshot, a shared screen or a history
     entry does not carry a live console credential. */
  useEffect(() => {
    if (token !== null) window.history.replaceState(null, '', window.location.pathname);
  }, [token]);

  useEffect(() => {
    if (token === null) return;
    let live = true;
    void api
      .post<{ email: string; displayName: string; role: string; passwordMinimum: number }>(
        '/api/auth/password-reset/preview',
        { token },
      )
      .then((p) => {
        if (live) setPreview(p);
      })
      .catch((error: unknown) => {
        if (!live) return;
        setDeadLink(
          error instanceof ApiRequestError
            ? error.message
            : 'That link has expired or has already been used.',
        );
      });
    return () => {
      live = false;
    };
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || token === null || preview === null) return;
    if (password.length < preview.passwordMinimum) return;
    setBusy(true);
    setFailure(null);
    try {
      const answer = await api.post<{ sessionsEnded: number; trustedDevicesEnded: number }>(
        '/api/auth/password-reset/complete',
        { token, password },
      );
      setDone({
        sessionsEnded: answer.sessionsEnded,
        trustedDevicesEnded: answer.trustedDevicesEnded,
      });
    } catch (error) {
      setFailure(
        error instanceof ApiRequestError
          ? error.message
          : 'The password was not changed. Nothing on the account has moved.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (done !== null) {
    return (
      <AuthShell>
      <div className="stack signin-step">
        <h3>Password set</h3>
        <p className="text-subtle">
          {done.sessionsEnded === 0
            ? 'No other session was open.'
            : done.sessionsEnded === 1
              ? 'One other session was signed out.'
              : `${done.sessionsEnded} other sessions were signed out.`}
        </p>
        {done.trustedDevicesEnded > 0 && (
          <p className="text-subtle">
            {done.trustedDevicesEnded === 1
              ? 'One saved device will ask for a code again.'
              : `${done.trustedDevicesEnded} saved devices will ask for a code again.`}
          </p>
        )}
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Sign in with the new password. The console will ask for a code from your
            authenticator, as it always does — this link did not change it.
          </div>
        </div>
        <Button variant="primary" onClick={() => (window.location.href = '/')}>
          Go to sign in
        </Button>
      </div>
    </AuthShell>
    );
  }

  if (token === null) {
    return (
      <AuthShell>
      <div className="stack signin-step">
        <h3>That is not a reset link</h3>
        <p className="text-subtle">
          The address carries no token. Open the link from your email exactly as it was sent —
          copying only part of it leaves the token behind.
        </p>
        <Button onClick={() => (window.location.href = '/')}>Back to sign in</Button>
      </div>
    </AuthShell>
    );
  }

  if (deadLink !== null) {
    return (
      <AuthShell>
      <div className="stack signin-step">
        <h3>That link no longer works</h3>
        {/* ONE refusal for every reason — spent, expired, superseded, never
            issued, account since suspended. Distinguishing them would let
            somebody holding a dead token learn which kind of dead it was. */}
        <p className="text-subtle">{deadLink}</p>
        <p className="text-subtle">
          Asking for a new one also cancels any older link, so if you asked twice, only the most
          recent email works.
        </p>
        <Button variant="primary" onClick={() => (window.location.href = '/forgot-password')}>
          Ask for a new link
        </Button>
      </div>
    </AuthShell>
    );
  }

  if (preview === null) {
    return (
      <AuthShell>
      <div className="stack signin-step">
        <div className="skeleton" style={{ height: '120px' }}>
          &nbsp;
        </div>
      </div>
    </AuthShell>
    );
  }

  const tooShort = password.length > 0 && password.length < preview.passwordMinimum;

  return (
    <AuthShell>
      <form className="stack signin-step" onSubmit={(e) => void submit(e)} noValidate>
      <h3>Set a new password</h3>
      <p className="text-subtle">
        {preview.displayName} · {preview.email} · {preview.role}
      </p>

      <Field
        label="New password"
        hint={`At least ${preview.passwordMinimum} characters.`}
        {...(tooShort
          ? { error: `At least ${preview.passwordMinimum} characters — this account can move money.` }
          : {})}
      >
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </Field>

      {failure !== null && (
        <div className="notice notice-danger">
          <Icon name="warning" />
          <div>{failure}</div>
        </div>
      )}

      {/* Both surprises, before the press. */}
      <div className="notice notice-warn">
        <Icon name="warning" />
        <div>
          <p>
            <strong>Every other session ends.</strong> Anywhere this account is signed in stops
            working at the next click — including a second screen of your own.
          </p>
          <p>
            <strong>Saved devices will ask for a code again.</strong> Any browser where you
            ticked &ldquo;Trust this device&rdquo; goes back to asking for your authenticator.
            That is the point: a trusted browser skips the code, so a reset that left it trusted
            would leave it needing nothing but the new password.
            {/* The CONTROL is named rather than its duration repeated:
                `DEVICE_TRUST_DAYS` is configurable, and the sign-in checkbox
                already states a number, so a second copy here would be a second
                place for it to go stale. */}
          </p>
          <p>
            <strong>You are not signed in here.</strong> Setting a password proves you can read
            the email; signing in is a separate step, and the console will still ask for a code
            from your authenticator.
          </p>
        </div>
      </div>

      <Button
        variant="primary"
        block
        type="submit"
        busy={busy}
        disabled={password.length < preview.passwordMinimum}
      >
        Set the password
        <Icon name="arrow-right" />
      </Button>
      </form>
    </AuthShell>
  );
}
